import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { findDOMNode } from 'react-dom'
import { CSSTransition } from 'react-transition-group'
import classnames from 'classnames'
import Icon from '../Icon/Icon'
import Keycode from '../../lib/keycode'
import { tabbable } from 'tabbable'
import css from './style.module.sass'

export default class Modal extends Component {
  static propTypes = {
    handleDismiss: PropTypes.func, // Omit to make it so the modal can't be dismissed
    show: PropTypes.bool,
    padding: PropTypes.string,
    title: PropTypes.string,
    enableAutoFocus: PropTypes.bool,
    handleShown: PropTypes.func,
    handleHidden: PropTypes.func,
    error: PropTypes.bool
  }

  static defaultProps = {
    show: false,
    padding: '2.5em',
    enableAutoFocus: true,
    handleShown: () => {},
    handleHidden: () => {},
    error: false
  }

  handleOnClick () {
    const { store } = this.props
    store.setFormSubmissionModalDisplay()
  }

  render () {
    const {
      show,
      title,
      padding,
      children,
      error
    } = this.props

    let titleTag
    if (title) {
      const titleClasses = classnames(css.titlebar, {
        [css.error]: error
      })
      titleTag = (
        <div className={css.titleContainer}>
          {error && (
            <div className={css.errorIconContainer}>
              <Icon name='big-warning' className={css.errorIcon} />
            </div>
          )}
          <div
            ref='title'
            className={titleClasses}
            id='modalTitle'
          >
            {title}
          </div>
        </div>
      )
    }

    return (
      <CSSTransition
        in={show}
        classNames={{
          enter: css.enter,
          enterActive: css.enterActive,
          exit: css.leave,
          exitActive: css.leaveActive
        }}
        timeout={300}
        unmountOnExit
      >
        <div data-automation-name={`${title}_Modal`} className={css.wrapper}>
          <div className={css.veil} />
          <div
            ref='modal' className={css.container}
            role='dialog' aria-labelledby='modalTitle'
            onKeyDown={this.handleKeyDown.bind(this)}
          >
            {titleTag}
            <div className={css.content} style={{ padding: padding }}>
              {children}
            </div>
            {this.handleOnClick.bind(this) != null && (
              <Icon
                name='close' ref='close' className={css.close}
                tabIndex='0'
                id='closeModal'
                role='button'
                aria-label='close modal'
                onClick={this.handleOnClick.bind(this)}
                onKeyDown={evt => Keycode(evt).handleClickIntent(this.handleOnClick.bind(this))}
              />
            )}
          </div>
        </div>
      </CSSTransition>
    )
  }

  componentDidMount () {
    if (this.props.show) {
      this.handleShown()
    }
  }

  componentWillUnmount () {
    this.handleHidden()
  }

  componentDidUpdate (prevProps) {
    if (this.props.show && !prevProps.show) {
      this.handleShown()
    }
  }

  componentWillUpdate (nextProps) {
    if (!nextProps.show && this.props.show) {
      this.handleHidden()
    }
  }

  handleShown () {
    this.autoFocus()
    this.props.handleShown()
  }

  handleHidden () {
    this.restoreFocus()
    this.props.handleHidden()
  }

  handleKeyDown (evt) {
    Keycode(evt).on({
      esc: this.props.handleDismiss,
      tab: () => {
        // Prevent the user from tabbing to elements outside of the modal
        // We do this on keydown to catch the event before focus is advanced
        const tabbables = this.tabbableElements
        const first = tabbables[0]
        const last = tabbables[tabbables.length - 1]

        if (evt.target === last && !evt.shiftKey) {
          first.focus()
          evt.preventDefault()
        } else if (evt.target === first && evt.shiftKey) {
          last.focus()
          evt.preventDefault()
        }
      }
    })
  }

  get tabbableElements () {
    return tabbable(findDOMNode(this.refs.modal))
  }

  autoFocus () {
    this.previouslyFocusedElement = document.body
    if (this.props.enableAutoFocus && this.tabbableElements.length > 0) {
      findDOMNode(this.tabbableElements[0]).focus()
    }
  }

  restoreFocus () {
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus()
    }
  }
}

export const Paragraph = ({ children }) =>
  <p className={css.p}>{children}</p>
